import { Button, Table } from '@flos/react-ui';
import type { CoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import CoverageTableRow from '../CoverageTable/CoverageTableRow';
import {
    getDeductiblesText,
    handleMVPYoungDriverCoverageTerms,
    processBodyTerms
} from '../../utils/termsUtil';
import { useHippoText } from '../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { businessFlowSelector } from '../../recoil/selectors/getBusinessFlowSelector';
import { FLAVOUR_NAME_MAP } from '../../constants/coverable';
import { useCallback } from 'react';
import usePageNavigation from '../../hooks/usePageNavigation';
import { getKeyByValue } from '../../utils/objectUtil';
import { excludedFlavoursCoveragesConfig } from '../../config/excludedFlavorsConfig';
import { IS_OPC_EDIT_AVAILABLE, OPC_EDIT_LOBS } from '../../pages/OpcEntryPage/config/OpcConfig';
import { getProductName } from '../../utils/coverablesUtil';
import { MVP_YOUNG_DRIVER_COV } from '../../constants/coverage';
import { cloneDeep } from 'lodash';

const IncludedCoverages = ({
    includedCoverages,
    showCoverableDetailsUI,
    shouldRenderTitle = false,
    flavourName,
    coverableId,
    coverableStatus
}: {
    shouldRenderTitle?: boolean;
    showCoverableDetailsUI: boolean;
    includedCoverages: CoverageDTO[] | undefined;
    flavourName: string;
    coverableId: string;
    coverableStatus: string;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const businessFlow = useRecoilValue(businessFlowSelector);
    const { goToOPCEditCoveragePage } = usePageNavigation();
    const productName = getKeyByValue(FLAVOUR_NAME_MAP, flavourName.toLocaleLowerCase() ?? '');

    const shouldHideAddCoverageButton =
        productName === 'top_cho_insurance' ||
        excludedFlavoursCoveragesConfig.adjustCoverages.includes(flavourName);

    const navigateToAdjustExistingCoveragePage = useCallback((): void => {
        if (businessFlow !== 'DA') {
            return;
        }
        goToOPCEditCoveragePage(getProductName(flavourName), coverableId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessFlow, coverableId, flavourName]);

    const shouldShowAdjustButton = (): boolean => {
        return (
            businessFlow !== 'ONSA' &&
            !shouldHideAddCoverageButton &&
            coverableStatus === 'Added' &&
            IS_OPC_EDIT_AVAILABLE &&
            OPC_EDIT_LOBS.includes(getProductName(flavourName))
        );
    };

    return (
        <div className="included-coverages-container">
            {shouldRenderTitle && (
                <div className="u-display-flex@xs u-flex-justify-between@xs">
                    <h4>{getHippoText('DSS-SHOPPING-BASKET-SELECTED-COVERAGES-TITLE')}</h4>
                    {shouldShowAdjustButton() && (
                        <div className="u-display-inline-block">
                            <Button
                                onClick={navigateToAdjustExistingCoveragePage}
                                size="small"
                                className="u-spacing-stack-sm"
                            >
                                {getHippoText('DSS-SHOPPING-BASKET-ADJUST-COVERABLE')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
            <Table mobileDirection={showCoverableDetailsUI ? 'horizontal' : 'vertical'}>
                <Table.Body>
                    {includedCoverages?.map((coverage) => {
                        const currentCoverage = cloneDeep(coverage);
                        if (currentCoverage.type === MVP_YOUNG_DRIVER_COV) {
                            handleMVPYoungDriverCoverageTerms(
                                currentCoverage,
                                includedCoverages,
                                flavourName
                            );
                        }
                        const includedCoverageTerms = processBodyTerms(
                            currentCoverage?.terms ?? [{}],
                            flavourName
                        );
                        const { mainDeductibles, secondaryDeductibles } = includedCoverageTerms;
                        const deductibleTerms = mainDeductibles.concat(secondaryDeductibles);
                        return (
                            <CoverageTableRow
                                coverage={currentCoverage}
                                iconProps={
                                    !showCoverableDetailsUI
                                        ? {
                                              shape: 'circle-check',
                                              className: 'has-fill is-green'
                                          }
                                        : undefined
                                }
                                deductibleText={getDeductiblesText(
                                    currentCoverage,
                                    mainDeductibles,
                                    secondaryDeductibles
                                )}
                                key={currentCoverage.name}
                                deductibleTerms={deductibleTerms}
                                shouldAlignTextLeft={showCoverableDetailsUI}
                                showEarlierEffectiveDates={showCoverableDetailsUI}
                            />
                        );
                    })}
                </Table.Body>
            </Table>
        </div>
    );
};

export default IncludedCoverages;
